// NotificationHistory.jsx — admin view of every push notification sent by the dashboard (push_log).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { RefreshCw, Bell, Search, Download, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../context/AuthContext';

// push_log.ts is UTC (SQLite datetime('now')) → show WIB.
const toWib = ts => {
  if (!ts) return '';
  const d = new Date(String(ts).replace(' ', 'T') + 'Z');
  if (isNaN(d)) return ts;
  return d.toLocaleString('en-GB', { timeZone: 'Asia/Jakarta', year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const isFailed = r => (r.failed > 0) || !!r.error;

export default function NotificationHistory() {
  const [data, setData] = useState({ items: [], total: 0, sources: [] });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [f, setF] = useState({ source: '', status: '', from: '', to: '', q: '' });
  const [qInput, setQInput] = useState('');
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = { limit: 2000 };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/api/push/log', { params });
      setData(r.data || { items: [], total: 0, sources: [] });
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => setF(p => ({ ...p, q: qInput })), 400); return () => clearTimeout(t); }, [qInput]);

  const stats = useMemo(() => {
    const it = data.items;
    return { total: data.total, failed: it.filter(isFailed).length, today: it.filter(r => toWib(r.ts).slice(0, 11) === toWib(new Date().toISOString().slice(0, 19).replace('T', ' ')).slice(0, 11)).length };
  }, [data]);

  const exportXlsx = () => {
    const rows = data.items.map(r => ({ ID: r.id, 'Time (WIB)': toWib(r.ts), Source: r.source, Title: r.title, Body: r.body, Apps: r.apps || '', Recipients: r.recipients, Sent: r.sent, Failed: r.failed, Error: r.error || '' }));
    const ws = XLSX.utils.json_to_sheet(rows); const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Notifications');
    XLSX.writeFile(wb, `notification-history-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const inp = 'border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200';

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center"><Bell className="w-5 h-5 text-brand-600" /></div>
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Notification History</h1>
            <p className="text-sm text-slate-500">All push notifications sent by the dashboard (last 2000)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={exportXlsx} disabled={!data.items.length} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"><Download className="w-4 h-4" />Export</button>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[['Matching', stats.total, 'text-slate-800'], ['Today', stats.today, 'text-brand-700'], ['Failed', stats.failed, 'text-rose-600']].map(([l, v, c]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-slate-500">{l}</div><div className={`text-2xl font-semibold ${c}`}>{v}</div></div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={qInput} onChange={e => setQInput(e.target.value)} placeholder="Search title, message, error…" className={`${inp} w-full pl-9`} />
        </div>
        <select value={f.source} onChange={e => set('source', e.target.value)} className={inp}>
          <option value="">All sources</option>
          {data.sources.map(s => <option key={s.source} value={s.source}>{s.source} ({s.n})</option>)}
        </select>
        <select value={f.status} onChange={e => set('status', e.target.value)} className={inp}>
          <option value="">All status</option><option value="ok">Delivered</option><option value="failed">Failed</option>
        </select>
        <input type="date" value={f.from} onChange={e => set('from', e.target.value)} className={inp} title="From (WIB)" />
        <input type="date" value={f.to} onChange={e => set('to', e.target.value)} className={inp} title="To (WIB)" />
        {(f.source || f.status || f.from || f.to || qInput) && (
          <button onClick={() => { setF({ source: '', status: '', from: '', to: '', q: '' }); setQInput(''); }} className="text-sm text-slate-500 hover:text-slate-700 px-2">Reset</button>
        )}
      </div>

      {err && <div className="bg-rose-50 text-rose-700 text-sm rounded-lg p-3">{err}</div>}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-2">Time (WIB)</th><th className="text-left px-4 py-2">Source</th><th className="text-left px-4 py-2">Title / Message</th><th className="text-left px-4 py-2">Apps</th><th className="text-center px-4 py-2">Sent</th><th className="text-center px-4 py-2">Status</th></tr>
            </thead>
            <tbody>
              {data.items.map(r => (
                <tr key={r.id} onClick={() => setSel(r)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer align-top">
                  <td className="px-4 py-2 whitespace-nowrap text-slate-600">{toWib(r.ts)}</td>
                  <td className="px-4 py-2 whitespace-nowrap"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">{r.source}</span></td>
                  <td className="px-4 py-2 max-w-xl"><div className="font-medium text-slate-800 truncate">{r.title}</div><div className="text-slate-500 truncate">{r.body}</div></td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.apps || '-'}</td>
                  <td className="px-4 py-2 text-center">{r.sent ?? 0}</td>
                  <td className="px-4 py-2 text-center">{isFailed(r)
                    ? <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><AlertTriangle className="w-3.5 h-3.5" />Failed</span>
                    : <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 className="w-3.5 h-3.5" />OK</span>}</td>
                </tr>
              ))}
              {!data.items.length && !loading && <tr><td colSpan={6} className="text-center text-slate-400 py-10">No notifications found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {sel && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setSel(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-auto p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-3">
              <h2 className="font-semibold text-slate-800">{sel.title}</h2>
              <button onClick={() => setSel(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
              <div><b>Time:</b> {toWib(sel.ts)} WIB</div><div><b>Source:</b> {sel.source}</div>
              <div><b>Apps:</b> {sel.apps || '-'}</div><div><b>Inbox recipients:</b> {sel.recipients}</div>
              <div><b>Sent:</b> {sel.sent ?? 0}</div><div><b>Failed:</b> {sel.failed ?? 0}</div>
            </div>
            {sel.error && <div className="bg-rose-50 text-rose-700 text-xs rounded p-2 whitespace-pre-wrap">{sel.error}</div>}
            <pre className="bg-slate-50 rounded-lg p-3 text-sm whitespace-pre-wrap font-sans text-slate-700">{sel.body}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
